import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../../../styles/SubscribeThematics.module.scss";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { useReducer, useEffect } from "react";

const backUrl = (path = "") => {
  if (!path) return "";
  return `${process.env.REACT_APP_NUVEE_BASE_URL.split("/api")[0]}${path}`;
}

const formatHightlightedContent = (highlightedContent) => {
  if (!highlightedContent) return "";
  const lineBreakClass = styles.lineBreak || "lineBreak";

  return highlightedContent
    .replace(/\n/g, `<span class="${lineBreakClass}"></span>`)
    .replace(/<span class=".*?"><\/span><span class=".*?"><\/span>/g, `<span class="${lineBreakClass}"></span>`);
};

const ACTIONS = Object.freeze({
  TOGGLE_DIALOG: "toggle_dialog",
  SELECT_DISEASE: "select_disease",
  DESELECT_DISEASE: "deselect_disease",
  TOGGLE_SELECTION: "toggle_selection",
  TOGGLE_LIMIT_DIALOG: "toggle_limit_dialog"
});

const diseaseReducer = (state, action) => {
  if (!action || !action.type) return state;

  switch (action.type) {
    case ACTIONS.TOGGLE_DIALOG:
      return {
        ...state,
        dialogOpen: action.payload?.open ?? !state.dialogOpen
      };
    case ACTIONS.SELECT_DISEASE:
      return {
        ...state,
        isSelected: true,
        dialogOpen: false
      };
    case ACTIONS.DESELECT_DISEASE:
      return {
        ...state,
        isSelected: false
      };
    case ACTIONS.TOGGLE_SELECTION:
      return {
        ...state,
        isSelected: !state.isSelected
      };
    case ACTIONS.TOGGLE_LIMIT_DIALOG:
      return {
        ...state,
        limitDialogOpen: action.payload?.open ?? !state.limitDialogOpen
      };
    default:
      return state;
  }
};

/**
 * @component DiseaseCard
 * @description
 * Displays a disease/thematic card with selection (checkbox or "Choose" button) and a detail dialog.
 * Enforces a maximum of 2 selected thematics: if the user tries to select more than 2, a warning popup appears.
 * The selection limit is checked both from the checkbox and the dialog's "Choose" button.
 * Closing the warning popup also closes the detail dialog if it is open.
 *
 * @param {Object} props
 * @param {Object} props.disease - Disease information object
 * @param {string} props.disease.id - Unique identifier for the disease
 * @param {string} [props.disease.name] - Name of the disease
 * @param {string} [props.disease.highlightedContent] - Highlighted HTML content
 * @param {string} [props.disease.tagline] - Short tagline for the disease
 * @param {Object} [props.disease.diseaseAvatar] - Avatar image object
 * @param {string} [props.disease.diseaseAvatar.url] - URL for the disease avatar image
 * @param {string} [props.disease.description] - Detailed description of the disease
 * @param {string} [props.disease.color] - Theme color for the disease card
 * @param {boolean} [props.selected] - Whether the disease is already selected
 * @param {function} [props.toggleDisease] - Callback to handle selection (id, newSelectedState) => boolean
 * @returns {JSX.Element}
 */
const DiseaseCard = ({ disease, selected, toggleDisease }) => {
  const {
    id,
    name = "", 
    highlightedContent = "", 
    tagline = "", 
    diseaseAvatar = {}, 
    description = "", 
    color = "#145a79" 
  } = disease || {};

  const [state, dispatch] = useReducer(diseaseReducer, {
    dialogOpen: false,
    limitDialogOpen: false,
    isSelected: Boolean(selected)
  });

  useEffect(() => {
    if (Boolean(selected) !== state.isSelected) {
      dispatch({
        type: selected ? ACTIONS.SELECT_DISEASE : ACTIONS.DESELECT_DISEASE
      });
    }
  }, [selected]);

  const handleOpenDialog = () => dispatch({ type: ACTIONS.TOGGLE_DIALOG, payload: { open: true } });
  const handleCloseDialog = () => dispatch({ type: ACTIONS.TOGGLE_DIALOG, payload: { open: false } });
  const handleOpenLimitDialog = () => dispatch({ type: ACTIONS.TOGGLE_LIMIT_DIALOG, payload: { open: true } });
  const handleCloseLimitDialog = () => {
    dispatch({ type: ACTIONS.TOGGLE_LIMIT_DIALOG, payload: { open: false } });
    if (state.dialogOpen) {
      dispatch({ type: ACTIONS.TOGGLE_DIALOG, payload: { open: false } });
    }
  };

  const handleSelectDisease = () => {
    const newSelectedState = true;

    const isAllowed = toggleDisease ? toggleDisease(id, newSelectedState) : true;

    if (isAllowed) {
      dispatch({ type: ACTIONS.SELECT_DISEASE });
    }
  }

  const handleToggleSelection = () => {
    const newSelectedState = !state.isSelected;

    if (newSelectedState && !state.isSelected) {
      const currentSelectedCount = document.querySelectorAll('input[type="checkbox"]:checked').length;
      if (currentSelectedCount > 2) {
        handleOpenLimitDialog();
        return;
      }
    }

    const isAllowed = toggleDisease ? toggleDisease(id, newSelectedState) : true;

    if (isAllowed) {
      dispatch({ type: ACTIONS.TOGGLE_SELECTION });
    }
  }

  const handleChooseButtonClick = () => {
    const currentSelectedCount = document.querySelectorAll('input[type="checkbox"]:checked').length;
    if (currentSelectedCount >= 2) {
      handleOpenLimitDialog();
      return;
    }
    handleSelectDisease();
  };

  return (
    <>
      <div className={styles.diseaseContainer}>
        <div 
          className={`${styles.diseaseCard} ${state.isSelected ? styles.selected : ""}`}
          onClick={() => {
            if (description) {
              handleOpenDialog();
            } else {
              handleToggleSelection();
            }
          }}
          role="article"
          aria-label={`Maladie : ${name}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (description) {
                handleOpenDialog();
              } else {
                handleToggleSelection();
              }
            }
          }}
        >
          {diseaseAvatar?.url && (
            <div className={styles.diseaseImage}>
              <img
                src={backUrl(diseaseAvatar.url)}
                alt={`Illustration de ${name}`}
                loading="lazy"
              />
            </div>
          )}
          <div className={styles.diseaseTitle}>
            <h3>{name}</h3>
            {description && (
              <div 
                className={styles.info}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog();
                }}
                role="img"
                aria-label="Plus d'informations sur cette maladie"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenDialog()
                  }
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
              </div>
            )}
          </div>
          <div 
            className={styles.highlight} 
            dangerouslySetInnerHTML={{ __html: formatHightlightedContent(highlightedContent) }}
            tabIndex={0}
            aria-label={`Contenu détaillé sur ${name}`}
          />
          <div className={styles.taglineContainer}>
            <div className={styles.taglineImg}>
              <img
                src={"/images/liberals/tagline_arrow.svg"}
                alt={`Illustration de ${name}`}
                loading="lazy"
              />
            </div>
            <div className={styles.tagline}>{tagline}</div>
          </div>
        </div>
        <label
          htmlFor={`select-disease-${id}`}
          className={styles.chooseButton}
        >
          <p>Choisir</p>  
          <input 
            type="checkbox"
            checked={state.isSelected}
            onChange={handleToggleSelection}
            aria-label={`Sélectionner ${name}`}
            id={`select-disease-${id}`}
            tabIndex={0}
          />
        </label>
      </div>

      <Dialog 
        className={styles.dialog}
        onClose={handleCloseDialog}
        open={state.dialogOpen}
        style={{ "--card-color": color }}
        aria-labelledby="disease-dialog-title"
      >
        <DialogTitle id="disease-dialog-title" className={styles.dialogHeader}>
          {diseaseAvatar?.url && (
            <img src={backUrl(diseaseAvatar.url)} alt={`Illustration de ${name}`} />
          )}
          {name}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {description && (
            <div>
              <h2>Description:</h2>
              {description.split("\n").map((line, index) => (
                <p key={`${name}-description-${index}`}>{line}</p>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions 
          className={styles.dialogActions}
          onClose={handleCloseDialog}
          open={state.dialogOpen}
          style={{ "--card-color": color ?? "#145A79" }}
        >
          <button 
            className={styles.subscribeButton}
            onClick={state.isSelected ? handleCloseDialog : handleChooseButtonClick}
            aria-pressed={state.isSelected}
          >
            {state.isSelected ? "Compris" : "Choisir"}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={state.limitDialogOpen}
        onClose={handleCloseLimitDialog}
        aria-labelledby="limit-dialog-title"
      >
        <DialogTitle id="limit-dialog-title">Attention</DialogTitle>
        <DialogContent>
          Vous ne pouvez sélectionner que 2 thématiques maximum&nbsp;!<br />
          Pour choisir une nouvelle thématique, veillez à décocher une thématique sélectionnée au préalable.
        </DialogContent>
        <DialogActions>
          <button className={styles.confirmCloseButton} onClick={handleCloseLimitDialog}>
            Compris
          </button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DiseaseCard;